import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A horizontal box plot is a variation of the box-and-whisker plot, where the data
          distribution is displayed along the horizontal axis instead of the vertical one. Similar
          to the vertical version, it shows key statistical metrics of a dataset, including the
          minimum, first quartile (Q1), median (Q2), third quartile (Q3), and maximum values, along
          with any potential outliers. The box represents the interquartile range (IQR), and the
          whiskers extend horizontally to show the range of data within 1.5 times the IQR. Outliers
          are marked as individual points outside the whiskers. Horizontal box plots are
          particularly useful for comparing the distributions of data across categories, making it
          easier to identify variations in data spread, central tendency, and any extreme values.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Horizontal box plots, or horizontal box-and-whisker plots, are powerful tools for
          summarizing and comparing data distributions. Here are some key uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Exploring Data Spread:</strong> They effectively represent the range, median,
            and quartiles of data, offering a comprehensive view of variability.
          </li>
          <li>
            <strong>Highlighting Outliers:</strong> Horizontal box plots make it easy to identify
            outliers, which appear as individual points beyond the whiskers.
          </li>
          <li>
            <strong>Comparing Categories:</strong> Their layout is ideal for comparing distributions
            across multiple categories, especially when there are numerous groups or labels.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Horizontal box plots are a useful tool for visualizing the distribution of data across
          different categories, but they come with several limitations that should be considered
          when deciding to use them:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Precision with Many Categories:</strong> Horizontal box plots can become
            cluttered when visualizing a large number of categories or data points, making it
            difficult to distinguish between them. The horizontal layout may limit space for clear
            labeling, resulting in a loss of precision in comparisons.
          </li>
          <li>
            <strong>Difficult to Interpret with Skewed Data:</strong> While box plots provide
            valuable insight into the distribution, they might not be the best choice for data with
            significant skewness or outliers. In such cases, the box plot can distort the perceived
            central tendency, leading to misinterpretation.
          </li>
          <li>
            <strong>Challenges with Small Data Sets:</strong> Horizontal box plots can lose their
            effectiveness when applied to small data sets, as they may not clearly depict meaningful
            spread or outliers, making it harder to derive actionable insights from the visualized
            data.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in horizontal box plots is key to clarity and comparison. Assigning
          distinct hues to data groups helps differentiate categories, while neutral or muted tones
          for box fills maintain focus on the data. Highlighting critical elements, such as medians
          or outliers, with bold or contrasting colors enhances interpretation. Consistent and
          thoughtful color choices ensure the plot is visually engaging and easy to understand.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Horizontal box plots effectively summarize data distributions, but they also present some
          unique challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Understanding Orientation:</strong> For some audiences, the horizontal layout
            might initially be less intuitive, especially if they are more familiar with vertical
            box plots.
          </li>
          <li>
            <strong>Overlap in Dense Data:</strong> When displaying multiple categories with
            overlapping ranges, it can be challenging to discern individual distributions clearly,
            leading to potential misinterpretations.
          </li>
          <li>
            <strong>Labeling Constraints:</strong> Long category labels can clash with the
            horizontal axis, making it difficult to present clear and uncluttered text while
            maintaining legibility.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
